/**
 * Export (SPEC §15, "never locked in"): a Logseq-compatible Markdown tree plus a JSON dump of
 * every live node.
 *
 * Markdown, as Logseq's file-based graph reads it:
 * - `journals/YYYY_MM_DD.md` per day with content, `pages/<title>.md` per page or tag;
 * - blocks as `- text`, children indented with tabs, further lines of a block indented to the text;
 * - fields as `name:: value` under the block (refs as `[[Title]]`), page tags as `tags::` and page
 *   fields as page properties (lines before the first block), `title::` when the file name had to
 *   differ from the title, `collapsed:: true`, and `id::` on blocks that `((ref))`s point at;
 * - `@Name` becomes `[[Name]]`, `CANCELLED` becomes Logseq's `CANCELED`.
 */
import { SETTINGS_ID } from './builtins.ts'
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import { parse, walkInlines } from './markdown.ts'
import type { Node } from './model.ts'
import { refsOf } from './refs.ts'
import { SCHEMA_FIELD } from './schema.ts'
import { fieldsOf, type TagEnv } from './tags.ts'

export interface ExportFile {
  readonly path: string
  readonly content: string
}

/** Logseq-style property name: lower case, words joined by dashes. */
export function propertyName(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '') || 'field'
  )
}

/** A file name for a title (Logseq's triple-lowbar format; characters file systems refuse are %-encoded). */
export function pageFileName(title: string): string {
  const name = title
    .replace(/\//g, '___')
    .replace(/[<>:"\\|?*\p{Cc}%]/gu, (c) => `%${c.codePointAt(0)!.toString(16).toUpperCase().padStart(2, '0')}`)
    .replace(/^\.+/, (m) => '%2E'.repeat(m.length))
    .replace(/[. ]+$/, (m) => m.replace(/\./g, '%2E').replace(/ /g, '%20'))
  return `${name || 'untitled'}.md`
}

/** Content with Solar-only syntax turned into what Logseq reads. */
export function toLogseqContent(content: string): string {
  let out = content.replace(/^CANCELLED(?= |$)/, 'CANCELED')
  const mentions = refsOf(out).filter((r) => r.kind === 'mention')
  for (let i = mentions.length - 1; i >= 0; i--) {
    const r = mentions[i]!
    out = `${out.slice(0, r.from)}[[${r.title}]]${out.slice(r.to)}`
  }
  return out
}

function valueText(g: Graph, v: unknown): string {
  const one = (x: unknown): string => {
    if (typeof x === 'string') {
      const n = g.get(x)
      if (n?.title) return `[[${n.title}]]`
      return x.replace(/\n/g, ' ')
    }
    return x === null || x === undefined ? '' : String(x)
  }
  return Array.isArray(v) ? v.map(one).join(', ') : one(v)
}

/** `name:: value` lines for a node's fields (by schema name; system fields left out). */
function fieldLines(g: Graph, env: TagEnv, n: Node): string[] {
  const lines: string[] = []
  const named = new Set<string>()
  for (const { field } of fieldsOf(env, n)) {
    named.add(field.id)
    const v = n.props[field.id]
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) continue
    if (field.hidden) continue
    lines.push(`${propertyName(field.name)}:: ${valueText(g, v)}`)
  }
  for (const [k, v] of Object.entries(n.props)) {
    if (named.has(k) || k.startsWith('sys:') || v === null || v === undefined) continue
    lines.push(`${propertyName(k)}:: ${valueText(g, v)}`)
  }
  return lines
}

/** Ids that some text refers to with `((id))` or an embed: these blocks get an `id::` line. */
function referencedBlocks(g: Graph): Set<NodeId> {
  const ids = new Set<NodeId>()
  for (const n of g.nodes()) {
    if (n.deletedAt !== null || (!n.content.includes('((') && !n.content.includes('{{'))) continue
    for (const x of walkInlines(parse(n.content))) {
      if (x.t === 'blockref') ids.add(x.id)
      else if (x.t === 'embed' && x.id) ids.add(x.id)
    }
  }
  return ids
}

function blockLines(g: Graph, env: TagEnv, id: NodeId, depth: number, refd: ReadonlySet<NodeId>, out: string[]): void {
  for (const k of g.children(id)) {
    const n = g.get(k)
    if (!n || n.deletedAt !== null) continue
    const indent = '\t'.repeat(depth)
    const [first, ...rest] = toLogseqContent(n.content).split('\n')
    out.push(`${indent}- ${first ?? ''}`)
    const more = [...rest]
    if (n.collapsed && g.children(k).some((c) => g.isLive(c))) more.push('collapsed:: true')
    if (refd.has(k)) more.push(`id:: ${k}`)
    more.push(...fieldLines(g, env, n))
    for (const line of more) out.push(`${indent}  ${line}`)
    blockLines(g, env, k, depth + 1, refd, out)
  }
}

/** The Markdown tree: one file per day with content and per page or tag with something in it. */
export function exportMarkdown(g: Graph, env: TagEnv): ExportFile[] {
  const files: ExportFile[] = []
  const refd = referencedBlocks(g)
  const used = new Set<string>()
  const roots = [...g.nodes()].filter((n) => n.deletedAt === null && n.parentId === null && n.id !== SETTINGS_ID)
  roots.sort((a, b) => (a.date ?? a.title ?? '').localeCompare(b.date ?? b.title ?? ''))
  for (const n of roots) {
    const body: string[] = []
    blockLines(g, env, n.id, 0, refd, body)
    if (n.kind === 'journal' && n.date) {
      if (body.length === 0) continue
      files.push({ path: `journals/${n.date.replace(/-/g, '_')}.md`, content: `${body.join('\n')}\n` })
      continue
    }
    if (!n.title) continue
    const props: string[] = []
    let file = pageFileName(n.title)
    for (let i = 2; used.has(file.toLowerCase()); i++) file = pageFileName(`${n.title} (${i})`)
    used.add(file.toLowerCase())
    if (file !== `${n.title}.md`) props.push(`title:: ${n.title}`)
    const tagTitles = n.tags.map((t) => g.get(t)?.title).filter((t): t is string => !!t)
    if (tagTitles.length) props.push(`tags:: ${tagTitles.join(', ')}`)
    props.push(...fieldLines(g, env, n))
    if (body.length === 0 && props.length === 0 && n.kind !== 'tag') continue
    files.push({ path: `pages/${file}`, content: `${[...props, ...(props.length && body.length ? [''] : []), ...body].join('\n')}\n` })
  }
  return files
}

/** Every live node, fields and schemas included, for a lossless restore. */
export function exportJson(g: Graph, exportedAt: number): string {
  const nodes = [...g.nodes()].filter((n) => n.deletedAt === null)
  nodes.sort((a, b) => (a.id < b.id ? -1 : 1))
  return JSON.stringify({ format: 'solar2-export', version: 1, exportedAt: new Date(exportedAt).toISOString(), schemaField: SCHEMA_FIELD, nodes }, null, 1)
}
